import Hls, { type ErrorData } from "hls.js";
import { ref, onUnmounted } from "vue";

/**
 * HLS.js audio playback composable for adaptive bitrate streaming.
 *
 * Provides HLS support for browsers that don't natively support HLS (Chrome, Firefox)
 * while falling back to native HLS for Safari. Also provides graceful fallback to
 * direct MP3 playback if HLS fails.
 *
 * @example
 * ```ts
 * const { attachHls, detachHls, isHlsSupported, hlsInstance } = useHlsAudio();
 *
 * // Attach HLS to an audio element
 * const success = attachHls(audioElement, hlsUrl);
 * if (!success) {
 *   // Fall back to MP3
 *   audioElement.src = mp3Url;
 * }
 *
 * // Clean up when done
 * detachHls();
 * ```
 */
export function useHlsAudio() {
  const hlsInstance = ref<Hls | null>(null);
  const isHlsActive = ref(false);
  const currentAudioElement = ref<HTMLAudioElement | null>(null);

  /**
   * Check if HLS.js is supported in the current browser.
   * Returns true for Chrome, Firefox, Edge (Chromium), etc.
   */
  const isHlsSupported = Hls.isSupported();

  /**
   * Check if the browser has native HLS support (Safari, iOS).
   */
  function hasNativeHlsSupport(audio: HTMLAudioElement): boolean {
    return audio.canPlayType("application/vnd.apple.mpegurl") !== "";
  }

  /**
   * Attach HLS.js to an audio element for adaptive bitrate streaming.
   *
   * @param audio - The HTMLAudioElement to attach HLS to
   * @param hlsUrl - The HLS master playlist URL (m3u8)
   * @param onError - Optional callback for HLS errors (for fallback handling)
   * @returns true if HLS was successfully attached, false if fallback needed
   */
  function attachHls(
    audio: HTMLAudioElement,
    hlsUrl: string,
    onError?: (error: ErrorData) => void
  ): boolean {
    // Store reference to audio element
    currentAudioElement.value = audio;

    // Try HLS.js first (Chrome, Firefox, Edge)
    if (isHlsSupported) {
      // Destroy any existing instance
      if (hlsInstance.value) {
        hlsInstance.value.destroy();
      }

      const hls = new Hls({
        // Enable web workers for better performance
        enableWorker: true,
        // Start with lowest quality for fast initial load
        startLevel: 0,
        // Auto level selection based on bandwidth
        autoStartLoad: true,
        // Low latency mode off (VOD content)
        lowLatencyMode: false,
        // Buffer settings for smooth playback
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Backoff settings for error recovery
        fragLoadingMaxRetry: 3,
        manifestLoadingMaxRetry: 3,
        levelLoadingMaxRetry: 3,
      });

      // Attach to audio element
      hls.attachMedia(audio);

      // Load the source
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(hlsUrl);
        isHlsActive.value = true;
      });

      // Handle manifest parsed (playback can start)
      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        console.log(
          `[useHlsAudio] HLS manifest parsed, ${data.levels.length} quality levels`
        );
      });

      // Handle errors
      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.warn("[useHlsAudio] HLS error:", data.type, data.details);

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error("[useHlsAudio] Fatal network error, attempting recovery");
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error("[useHlsAudio] Fatal media error, attempting recovery");
              hls.recoverMediaError();
              break;
            default:
              console.error("[useHlsAudio] Fatal error, destroying HLS instance");
              detachHls();
              onError?.(data);
              break;
          }
        }
      });

      // Handle level switching (quality changes)
      hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        const level = hls.levels[data.level];
        if (level) {
          console.log(
            `[useHlsAudio] Quality switched to level ${data.level}: ${level.bitrate}bps`
          );
        }
      });

      hlsInstance.value = hls;
      return true;
    }

    // Try native HLS (Safari, iOS)
    if (hasNativeHlsSupport(audio)) {
      audio.src = hlsUrl;
      isHlsActive.value = true;
      console.log("[useHlsAudio] Using native HLS support");
      return true;
    }

    // HLS not supported
    console.warn("[useHlsAudio] HLS not supported in this browser");
    return false;
  }

  /**
   * Detach HLS from the audio element and clean up resources.
   */
  function detachHls(): void {
    if (hlsInstance.value) {
      hlsInstance.value.destroy();
      hlsInstance.value = null;
    }
    isHlsActive.value = false;
    currentAudioElement.value = null;
  }

  /**
   * Get the current HLS level/quality being played.
   * Returns -1 if HLS is not active or using auto selection.
   */
  function getCurrentLevel(): number {
    return hlsInstance.value?.currentLevel ?? -1;
  }

  /**
   * Get available quality levels.
   * Returns an empty array if HLS is not active.
   * Each level has properties like `bitrate`, `height`, `width`, etc.
   */
  function getLevels(): Array<{ bitrate: number; height?: number; width?: number }> {
    return (hlsInstance.value?.levels ?? []).map((level) => ({
      bitrate: level.bitrate,
      height: level.height,
      width: level.width,
    }));
  }

  /**
   * Set the quality level manually.
   * Pass -1 for auto selection.
   */
  function setLevel(level: number): void {
    if (hlsInstance.value) {
      hlsInstance.value.currentLevel = level;
    }
  }

  /**
   * Force auto quality selection.
   */
  function setAutoQuality(): void {
    if (hlsInstance.value) {
      hlsInstance.value.currentLevel = -1;
    }
  }

  // Clean up on unmount
  onUnmounted(() => {
    detachHls();
  });

  return {
    /** Whether HLS.js is supported in the current browser */
    isHlsSupported,
    /** Whether HLS is currently active */
    isHlsActive,
    /** The current HLS.js instance (null if not active) */
    hlsInstance,
    /** The current audio element with HLS attached */
    currentAudioElement,
    /** Attach HLS to an audio element */
    attachHls,
    /** Detach HLS and clean up */
    detachHls,
    /** Get current quality level */
    getCurrentLevel,
    /** Get available quality levels */
    getLevels,
    /** Set quality level manually (-1 for auto) */
    setLevel,
    /** Force auto quality selection */
    setAutoQuality,
  };
}

export type UseHlsAudioReturn = ReturnType<typeof useHlsAudio>;
